import type { Persona } from '@repo/contracts';

import { apiFetch } from './client';

/**
 * Personas: one pool per account, a panel per project.
 *
 * `?projectId=` reads one project's panel; without it, the whole account pool
 * (what "Add from other projects" picks from). See the entity's own comment.
 *
 * Everything imported from `@repo/contracts` here is **type-only**. The
 * package's `.` export is compiled CJS, and a linked package's CJS dist cannot
 * be statically analysed for named exports when Vite loads it as ESM — a
 * runtime value (`PERSONA_RESPONSE_TAGS`, say) imported from it resolves and
 * then throws "does not provide an export named …" in the browser. Types are
 * erased before that can happen; values are not.
 */

export const personaKeys = {
  all: ['personas'] as const,
  /** One project's panel. */
  panel: (projectId: string) =>
    [...personaKeys.all, 'panel', projectId] as const,
  /** Every persona the account has, across projects. */
  pool: () => [...personaKeys.all, 'pool'] as const,
};

/** One project's panel, or the whole account pool without a project. */
export function listPersonas(projectId?: string): Promise<Persona[]> {
  return apiFetch<Persona[]>(
    projectId
      ? `/canvas/personas?projectId=${encodeURIComponent(projectId)}`
      : '/canvas/personas',
  );
}

/** Brings account personas onto a project's panel, or takes them off it.
 *  Taking somebody off never deletes them. Answers with the panel after. */
export function updateProjectPersonas(
  projectId: string,
  payload: { add?: string[]; remove?: string[] },
): Promise<Persona[]> {
  return apiFetch<Persona[]>(`/canvas/projects/${projectId}/personas`, {
    body: JSON.stringify(payload),
    method: 'PATCH',
  });
}

/** More people, written from a screen of a product. */
export function suggestPersonas(payload: {
  captureId: string;
  count?: number;
}): Promise<Persona[]> {
  return apiFetch<Persona[]>('/canvas/personas/suggest', {
    body: JSON.stringify(payload),
    method: 'POST',
  });
}

/** Toggle into or out of the panel, retire (`archived`), or edit. */
export function updatePersona(
  id: string,
  patch: {
    active?: boolean;
    archived?: boolean;
    description?: string;
    name?: string;
    title?: string;
  },
): Promise<Persona> {
  return apiFetch<Persona>(`/canvas/personas/${id}`, {
    body: JSON.stringify(patch),
    method: 'PATCH',
  });
}
